import { useRef, useState } from 'react';
import { BottomSheet } from '@/ui/components';
import { ArrowRightIcon } from '@/assets/icons';
import { warning } from '@/assets/images/emojis';
import { Button } from '@/ui/components/Button';
import styles from './MultipleLoanWarningModal.module.scss';

export interface MultipleLoanWarningModalProps {
  onConfirm: () => void;
  maxContracts: number;
  durationDays: number;
  closedContracts: number;
}

export function useMultipleLoanWarningModal({
  onConfirm,
  maxContracts,
  durationDays,
  closedContracts,
}: MultipleLoanWarningModalProps) {
  const [isOpen, setIsOpen] = useState(false);
  const cancelButtonRef = useRef<HTMLButtonElement>(null);

  const handleConfirm = () => {
    onConfirm();
    setIsOpen(false);
  };

  const handleCancel = () => {
    setIsOpen(false);
  };

  const open = () => {
    setIsOpen(true);
  };

  const close = () => {
    setIsOpen(false);
  };

  const title = 'Atenție!';
  const description = `Ai închis deja ${closedContracts} ${
    closedContracts === 1 ? 'credit' : 'credite'
  } la noi. Îți reamintim că poți lua maxim ${maxContracts} credite în decurs de ${durationDays} de zile.`;

  const content = (
    <div className={styles.container}>
      {/* Emoji */}
      <div className={styles.emoji}>
        <img src={warning} alt="Warning" className={styles.emojiImage} />
      </div>

      {/* Content */}
      <div className={styles.content}>
        <h2 className={styles.title}>{title}</h2>
        <p className={styles.description}>{description}</p>
      </div>
    </div>
  );

  const footer = (
    <div className={styles.buttons}>
      <Button
        trackingId="dismiss_multiple_loan_warning"
        variant="outlined"
        onClick={handleCancel}
        ref={cancelButtonRef}
        className={styles.buttonBack}
        text="Înapoi"
      />
      <Button
        trackingId="confirm_multiple_loan_warning"
        variant="filled"
        onClick={handleConfirm}
        text="Continuă"
        rightIcon={<ArrowRightIcon />}
        className={styles.buttonContinue}
      />
    </div>
  );

  return {
    open,
    close,
    modal: (
      <BottomSheet
        open={isOpen}
        onOpenChange={setIsOpen}
        ariaLabel={title}
        ariaDescription={description}
        enableDrag={true}
        closeOnOverlay={true}
        footer={footer}
        initialFocusRef={cancelButtonRef}
      >
        {content}
      </BottomSheet>
    ),
  };
}
